import { useClient } from '@cortex/react-query';
import type { WorkflowVersion, WorkflowVersionDiff } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  diffVersionSettings,
  type RefResolver,
  type VersionSettingsDiff,
} from '../../lib/workflow-graph-diff';

import { WorkflowGraphDiff, type DiffMode } from './WorkflowGraphDiff';

type ModelConfigLite = { id: string; name: string; provider: string; modelId: string };
type AgentLite = { id: string; name: string };

/**
 * Builds a resolver that turns opaque config id references into readable
 * labels for the diff: `modelConfigId` → "Opus 4.1 (anthropic · …)",
 * `agentId` → the agent's name. Without this, swapping an llm-agent node's
 * model just shows one cuid replacing another — useless to a reviewer.
 * Reuses the same cached queries the agent/model pages use.
 */
function useGraphRefResolver(): RefResolver {
  const client = useClient();
  const models = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs').json<{ items: ModelConfigLite[] }>(),
    staleTime: 60_000,
  });
  const agents = useQuery({
    queryKey: ['agents', 'for-node-config'],
    queryFn: () => client.http.get('v1/agents?limit=200').json<{ items: AgentLite[] }>(),
    staleTime: 60_000,
  });
  return useMemo<RefResolver>(() => {
    const modelById = new Map((models.data?.items ?? []).map((m) => [m.id, m]));
    const agentById = new Map((agents.data?.items ?? []).map((a) => [a.id, a]));
    return (field, value) => {
      if (field === 'modelConfigId') {
        const m = modelById.get(value);
        return m ? `${m.name} (${m.provider} · ${m.modelId})` : null;
      }
      if (field === 'agentId') {
        const a = agentById.get(value);
        return a ? a.name : null;
      }
      return null;
    };
  }, [models.data, agents.data]);
}

/**
 * `WorkflowVersion.attribution` is free-form. Detached / refinement-candidate
 * versions stamp `{ basedOnVersionId: <id> }` — read it defensively because
 * legacy rows carry other shapes. Returns the id only when it's a string.
 */
function readBasedOnVersionId(
  attribution: Record<string, unknown> | null | undefined,
): string | null {
  if (!attribution) return null;
  const value = (attribution as { basedOnVersionId?: unknown }).basedOnVersionId;
  return typeof value === 'string' ? value : null;
}

/**
 * The version a diff should compare `target` against: the version it was
 * explicitly forked from (`attribution.basedOnVersionId`) when that row is
 * present, otherwise the highest version number strictly below the target.
 * `null` when there is no earlier version (the target is the first cut).
 *
 * Resolving against the actual version list — rather than `versionNumber - 1`
 * — matters because numbers are sparse: detached/archived versions bump the
 * counter, so the immediate predecessor number may not exist.
 */
export function resolvePreviousVersionNumber(
  target: WorkflowVersion,
  all: readonly WorkflowVersion[],
): number | null {
  const basedOnId = readBasedOnVersionId(target.attribution);
  if (basedOnId) {
    const basedOn = all.find((v) => v.id === basedOnId);
    if (basedOn) return basedOn.versionNumber;
  }
  let prev: number | null = null;
  for (const v of all) {
    if (v.versionNumber < target.versionNumber && (prev === null || v.versionNumber > prev)) {
      prev = v.versionNumber;
    }
  }
  return prev;
}

/**
 * Fetches and renders the git-diff-style delta between two versions of a
 * workflow. `from`/`to` are version NUMBERS (the diff endpoint's contract).
 * Presentational rendering lives in `WorkflowGraphDiff`; this wrapper only
 * owns the query.
 */
export function WorkflowVersionDiffView({
  workflowId,
  fromVersionNumber,
  toVersionNumber,
  mode,
  onModeChange,
}: {
  workflowId: string;
  fromVersionNumber: number;
  toVersionNumber: number;
  mode?: DiffMode;
  onModeChange?: (m: DiffMode) => void;
}) {
  const client = useClient();
  const diff = useQuery({
    queryKey: ['workflow-version-diff', workflowId, fromVersionNumber, toVersionNumber],
    queryFn: () =>
      client.http
        .get(`v1/workflows/${workflowId}/versions/${fromVersionNumber}/diff/${toVersionNumber}`)
        .json<WorkflowVersionDiff>(),
    staleTime: 30_000,
  });
  // The diff endpoint reports nodes and edges only, so a version cut by the
  // Schema panel (which writes `inputSchema` / `outputSchema` / `outputMapping`
  // / dedup settings onto the version row and touches no node) comes back
  // empty. Those columns already ride along on the versions list, so read the
  // two rows from it and diff them client-side — same query key the Versions
  // panel populates, so this is normally a cache hit rather than a fetch.
  const versions = useQuery({
    queryKey: ['workflow-versions', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}/versions`).json<WorkflowVersion[]>(),
    staleTime: 30_000,
  });
  // `null` here is load-bearing: it means "compared, nothing changed". When the
  // two rows can't be read at all we must NOT collapse that to `null`, or the
  // renderer would state "No changes between vX and vY" on the strength of the
  // graph alone. `settingsComparable` carries that distinction so the empty
  // state can narrow its claim instead of overreaching.
  const { settingsDiff, settingsComparable, compareError } = useMemo((): {
    settingsDiff: VersionSettingsDiff | null;
    settingsComparable: boolean;
    compareError: unknown;
  } => {
    const all = versions.data ?? [];
    const before = all.find((v) => v.versionNumber === fromVersionNumber);
    const after = all.find((v) => v.versionNumber === toVersionNumber);
    // List still loading, request failed, or a version is no longer present.
    if (!before || !after)
      return { settingsDiff: null, settingsComparable: false, compareError: null };
    try {
      return {
        settingsDiff: diffVersionSettings(before, after),
        settingsComparable: true,
        compareError: null,
      };
    } catch (err) {
      // `canonicalJson` recurses, so a pathologically deep schema (~10k levels)
      // or a cyclic reference throws `RangeError`. Neither survives a JSON
      // round-trip from the API, so this is a backstop rather than a live path —
      // but an uncaught throw here tears down the React subtree, and "couldn't
      // compare" is a state this component already knows how to express.
      return { settingsDiff: null, settingsComparable: false, compareError: err };
    }
  }, [versions.data, fromVersionNumber, toVersionNumber]);
  const resolveRef = useGraphRefResolver();

  return (
    <WorkflowGraphDiff
      data={diff.data}
      isLoading={diff.isLoading}
      error={diff.error}
      resolveRef={resolveRef}
      mode={mode}
      onModeChange={onModeChange}
      settingsDiff={settingsDiff}
      settingsComparable={settingsComparable}
      // A comparison failure is more specific than a fetch failure, so prefer it.
      settingsError={compareError ?? versions.error}
    />
  );
}

/**
 * Graph diff for an experiment's candidate version vs. the version it was
 * forked from. Resolves both version numbers from the workflow's version
 * list, then defers to `WorkflowVersionDiffView`. Used by the experiment
 * detail modal and the manual refinement-run result.
 */
export function ExperimentGraphDiff({
  workflowId,
  candidateVersionId,
}: {
  workflowId: string;
  candidateVersionId: string;
}) {
  const client = useClient();
  const versions = useQuery({
    queryKey: ['workflow-versions', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}/versions`).json<WorkflowVersion[]>(),
  });

  if (versions.isLoading) {
    return (
      <div className="t-secondary t-sm" style={{ padding: 'var(--sp-2)' }}>
        Loading changes…
      </div>
    );
  }
  if (versions.error) {
    return (
      <div className="alert alert--danger t-sm" style={{ margin: 0, padding: 'var(--sp-2)' }}>
        Failed to load versions: {(versions.error as Error).message}
      </div>
    );
  }

  const all = versions.data ?? [];
  const candidate = all.find((v) => v.id === candidateVersionId);
  if (!candidate) {
    return (
      <p className="t-secondary t-xs" style={{ margin: 0 }}>
        Candidate version is no longer available.
      </p>
    );
  }
  const previous = resolvePreviousVersionNumber(candidate, all);
  if (previous === null) {
    return (
      <p className="t-secondary t-xs" style={{ margin: 0 }}>
        v{candidate.versionNumber} is the first version — nothing to compare against.
      </p>
    );
  }

  return (
    <WorkflowVersionDiffView
      workflowId={workflowId}
      fromVersionNumber={previous}
      toVersionNumber={candidate.versionNumber}
    />
  );
}
